import { Box } from '@atlaskit/primitives/compiled/box';

import { Flex } from '@atlaskit/primitives/compiled/flex';

import { Grid } from '@atlaskit/primitives/compiled/grid';

import { Pressable } from '@atlaskit/primitives/compiled/pressable';

import { Text } from '@atlaskit/primitives/compiled/text';

import { token } from '@atlaskit/tokens';

import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import { cssMap, cx } from '@atlaskit/css';

import { cssMap as cssMapUnbound } from '@compiled/react';

import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';

import { useCallback, useEffect, useRef, useState } from 'react';

import { JiraServiceManagementIcon } from '@atlaskit/logo/jira-service-management-icon';

import { AdControls } from '@post-office/components--ad-controls-sequoia';

import { FormattedMessage } from 'react-intl';

import { SlackDarkIcon } from '../../assets/slack-dark';

import { SlackLightIcon } from '../../assets/slack-light';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { useMessageEvent } from '@post-office/message-lifecycle';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { useMessageContext } from '@atlassian/post-office-context/message-context';

import { PremiumBenefitModalTrigger } from '@atlassian/edition-benefits-modal/premium-benefit-modal-trigger';

import { type JSX } from 'react/jsx-runtime';

import CrossIcon from '@atlaskit/icon/core/cross';

const slackIconMap: Record<'dark' | 'light', () => JSX.Element> = {
	dark: SlackDarkIcon,
	light: SlackLightIcon,
};

const containerStyles = cssMap({
	root: {
		boxSizing: 'border-box',
		position: 'relative',
		minWidth: '112px',
		alignItems: 'center',
		userSelect: 'none',
		borderRadius: token('radius.medium'),
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.accent.purple.subtlest'),
		transition: 'background-color 0.2s ease-in-out',
		marginRight: token('space.100'),
	},
	rootFocused: {
		backgroundColor: token('color.background.accent.purple.subtlest.hovered'),
	},
});

const buttonStyles = cssMap({
	// This button is positioned to produce the visual appearance of nested
	// buttons whilst the elements are actually siblings in the DOM structure.
	root: {
		backgroundColor: 'transparent',
		borderRadius: token('radius.medium'),
		color: token('color.text.subtle'),
		alignItems: 'center',
		textAlign: 'start',
		// Extend the button to the full width of container
		width: '100%',
		transition: 'background-color 0.2s ease-in-out',
		// Notes:
		// - block padding is not strictly needed.
		// - it does cause some issues with "combine" styling on firefox@125; but not
		//   on firefox@137
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
	},
	rootFocused: {
		backgroundColor: token('color.background.accent.purple.subtlest.pressed'),
	},
});

const actionStyles = cssMap({
	root: {
		position: 'absolute',
		top: 0,
		right: 0,
		paddingBlockStart: token('space.050'),
		paddingInlineEnd: token('space.050'),
		// Hiding overflowing slot content to prevent content from adjacent slots overlapping when the menu item is constrained to a narrow width.
		overflow: 'hidden',
		'&:focus-within': {
			// To prevent the focus ring from being clipped, we are allowing content to overflow while focus is within the slot.
			overflow: 'initial',
		},
	},
});

const textStyles = cssMap({
	root: {
		paddingInlineStart: token('space.075'),
		gridTemplateColumns: '1fr minmax(0, auto)',
		gridTemplateRows: '1fr 1fr',
		gridTemplateAreas: '"label elem-after" "description elem-after"',
		columnGap: token('space.100'),
		'@media (min-width: 0rem) and (max-width: 29.99rem)': {
			gridTemplateAreas: '"label elem-after" "description description"',
		},
		'@media (min-width: 48rem) and (max-width: 63.99rem)': {
			gridTemplateAreas: '"label elem-after" "description description"',
		},
		'@media (min-width: 64rem) and (max-width: 89.99rem)': {
			gridTemplateAreas: '"label elem-after" "description description"',
		},
	},
	label: {
		gridArea: 'label',
	},
	description: {
		gridArea: 'description',
	},
});

const elemAfterStyles = cssMapUnbound({
	root: {
		gridArea: 'elem-after',
		paddingInlineEnd: token('space.250'),
		'&:focus-within': {
			// To prevent the focus ring from being clipped, we are allowing content to overflow while focus is within the slot.
			overflow: 'initial',
		},
	},
	jsmIcon: {
		// Have to use selectors here because the JiraServiceManagementIcon is a styled component and we need to target the span inside it.
		selectors: {
			span: {
				width: '48px',
				height: '48px',
				transform: 'rotate(-15deg)',
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100%',
					height: '100%',
					boxShadow: token('elevation.shadow.overlay'),
					borderRadius: token('radius.xlarge'),
				},
				'@media (min-width: 0rem) and (max-width: 29.99rem)': {
					width: '24px',
					height: '24px',
					'&::before': {
						borderRadius: token('radius.medium'),
					},
				},
				'@media (min-width: 48rem) and (max-width: 63.99rem)': {
					width: '24px',
					height: '24px',
					'&::before': {
						borderRadius: token('radius.medium'),
					},
				},
				'@media (min-width: 64rem) and (max-width: 89.99rem)': {
					width: '24px',
					height: '24px',
					'&::before': {
						borderRadius: token('radius.medium'),
					},
				},
			},
		},
		zIndex: 1,
	},
	jsmIconFocused: {
		transform: 'rotate(-7deg) scale(1.125)',
		transition: 'transform 0.2s ease-in-out',
	},
	jsmIconBlur: {
		transition: 'transform 0.2s ease-in-out',
	},
	slackIcon: {
		selectors: {
			div: {
				width: '48px',
				height: '48px',
				transform: 'rotate(30deg)',
				marginTop: token('space.050'),
				marginLeft: token('space.negative.025'),
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100%',
					height: '100%',
					boxShadow: token('elevation.shadow.overlay'),
					borderRadius: token('radius.xlarge'),
				},
				'@media (min-width: 0rem) and (max-width: 29.99rem)': {
					width: '24px',
					height: '24px',
					'&::before': {
						borderRadius: token('radius.medium'),
					},
				},
				'@media (min-width: 48rem) and (max-width: 63.99rem)': {
					width: '24px',
					height: '24px',
					'&::before': {
						borderRadius: token('radius.medium'),
					},
				},
				'@media (min-width: 64rem) and (max-width: 89.99rem)': {
					width: '24px',
					height: '24px',
					'&::before': {
						borderRadius: token('radius.medium'),
					},
				},
			},
		},
	},
	slackIconFocused: {
		transform: 'rotate(22deg) scale(1.125)',
		transition: 'transform 0.2s ease-in-out',
	},
	slackIconBlur: {
		transition: 'transform 0.2s ease-in-out',
	},
});

export type BannerProps = {
	dismiss: (messageInstanceId: string) => void;
	cloudId: string;
	edition: 'free' | 'standard' | 'premium' | 'unknown';
	shouldRenderIncidentsPremiumTrialNudge: boolean;
};

export const Banner = ({
	cloudId,
	edition,
	dismiss,
	shouldRenderIncidentsPremiumTrialNudge,
}: BannerProps): JSX.Element => {
	const { colorMode = 'light' } = useThemeObserver();
	const SlackIcon = slackIconMap[colorMode];
	const [isFocused, setIsFocused] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const { messageDismissed } = useMessageEvent();
	const { onDeclined } = usePostOfficeUserInteractions();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceId } = useMessageContext();

	useEffect(() => {
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'banner',
				actionSubjectId: 'premiumTrialNudge',
			},
			{
				edition,
				shouldRenderIncidentsPremiumTrialNudge,
			},
		);
	}, [edition, shouldRenderIncidentsPremiumTrialNudge, fireAnalyticsEvent]);

	const handleNoSlack = useCallback(async () => {
		await messageDismissed();
		void onDeclined({ interactionIdentifier: 'noSlack' });
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'noSlack',
			},
			{ isJsmGrowth390Enabled: 'true' },
		);

		if (messageInstanceId) {
			dismiss(messageInstanceId);
		}
	}, [fireAnalyticsEvent, messageDismissed, messageInstanceId, dismiss, onDeclined]);

	const handleFocusWithin = useCallback(() => {
		setIsFocused(true);
	}, []);

	const handleBlurWithin = useCallback((e: React.FocusEvent) => {
		const nextFocusTarget = e.relatedTarget as Node | null;
		// Keep icon focused if focus moved to another element inside the banner (e.g. AdControls)
		if (containerRef.current && nextFocusTarget && containerRef.current.contains(nextFocusTarget)) {
			return;
		}
		setIsFocused(false);
	}, []);

	const handleMouseEnter = useCallback(() => setIsFocused(true), []);
	const handleMouseLeave = useCallback(() => setIsFocused(false), []);

	return (
		<PremiumBenefitModalTrigger
			cloudId={cloudId}
			product="jira-service-management"
			variantId="incidents-slack-integration"
			touchpointId="issue-view-slack-integration"
			edition={edition}
		>
			{({ ref, props }) => (
				<Box
					ref={containerRef}
					xcss={cx(containerStyles.root, isFocused ? containerStyles.rootFocused : undefined)}
					onFocusCapture={handleFocusWithin}
					onBlurCapture={handleBlurWithin}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
					<Pressable
						{...props}
						ref={ref as React.RefObject<HTMLButtonElement>}
						xcss={cx(buttonStyles.root, isFocused ? buttonStyles.rootFocused : undefined)}
					>
						<Grid xcss={textStyles.root} alignItems="center">
							<Box xcss={textStyles.label}>
								<Text weight="bold" size="large" id="message-title">
									<FormattedMessage
										id="post-office.jsm-issue-view-details-section.title"
										defaultMessage="Resolve incidents on Slack with AI"
										description="Title of the placement"
									/>
								</Text>
							</Box>
							<Box xcss={textStyles.description}>
								<Text color="color.text.subtle">
									<FormattedMessage
										id="post-office.jsm-issue-view-details-section.description"
										defaultMessage="Update incidents from within Slack and summarize key events with AI."
										description="Description of the placement"
									/>
								</Text>
							</Box>
							<Flex xcss={elemAfterStyles.root} alignItems="center">
								<div
									css={[
										elemAfterStyles.jsmIcon,
										isFocused ? elemAfterStyles.jsmIconFocused : elemAfterStyles.jsmIconBlur,
									]}
								>
									<JiraServiceManagementIcon appearance="brand" />
								</div>
								<div
									css={[
										elemAfterStyles.slackIcon,
										isFocused ? elemAfterStyles.slackIconFocused : elemAfterStyles.slackIconBlur,
									]}
								>
									<div>
										<SlackIcon />
									</div>
								</div>
							</Flex>
						</Grid>
					</Pressable>
					<Flex xcss={actionStyles.root} alignItems="center" gap="space.050">
						<AdControls
							spacing="compact"
							iconSize="small"
							onDismissed={dismiss}
							showWhyAmISeeingThis={true}
							whyAmISeeingThisModalContent={{
								message: (
									<FormattedMessage
										id="post-office.jsm-issue-view-details-section.reasons-modal-explanation"
										defaultMessage="We think Service Collection Premium will improve your team's workflow. The recommendation is based on:"
										description="Explanation as to why the placeholder template is being shown"
									/>
								),
								reasons: [
									<FormattedMessage
										id="post-office.jsm-issue-view-details-section.reasons-modal-bullet-one"
										defaultMessage="your usage of Jira Service Management's Alerts and Incidents features"
										description="First bullet point of the reasons modal"
									/>,
								],
							}}
							additionalAnalyticsAttributes={{
								isJsmGrowth390Enabled: 'true',
							}}
							customDropdownItems={[
								<DropdownItem
									key="custom-dropdown-item-1"
									onClick={handleNoSlack}
									elemBefore={<CrossIcon label="" />}
								>
									<FormattedMessage
										id="post-office.jsm-issue-view-details-section.dismiss-button-one"
										defaultMessage="I don't use Slack"
										description="Text of the first dismiss button"
									/>
								</DropdownItem>,
							]}
						/>
					</Flex>
				</Box>
			)}
		</PremiumBenefitModalTrigger>
	);
};
